import { isCurator, type UserRole } from '@dyeatlas/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface CurrentUser {
  id: string
  email: string
  name: string
  firstName: string | null
  lastName: string | null
  role: UserRole
  accountStatus: 'pending' | 'active' | 'rejected' | 'suspended'
  emailVerified: boolean
  institutionId: string | null
}

export const currentUserKey = ['me'] as const

/**
 * Session source of truth for the client.
 *
 * Deliberately not Better Auth's `useSession`: the session cookie is cached for up
 * to five minutes, and `accountStatus` and `role` must reflect the database so an
 * approval or suspension takes effect on the next navigation.
 */
export function useCurrentUser() {
  const query = useQuery({
    queryKey: currentUserKey,
    queryFn: () => api.get<{ user: CurrentUser | null }>('/me'),
    staleTime: 60_000,
  })

  return {
    user: query.data?.user ?? null,
    isLoading: query.isLoading,
    isAdmin: query.data?.user?.role === 'admin',
    /** Super users and admins: they review submissions and may add references. */
    isCurator: isCurator(query.data?.user?.role),
    isActive: query.data?.user?.accountStatus === 'active',
  }
}

export function useRefreshCurrentUser() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: currentUserKey })
}
